import { router, usePage } from "@inertiajs/react"
import { useCallback, useMemo } from "react"

import { candidatesPath } from "@/routes"
import type {
  CandidateFilters,
  CandidateSort,
  CandidateStatus,
} from "@/types/candidates"

/**
 * Detect whether the current URL carries `?country=` with an empty
 * value. BE's CTPO-locked default-France behavior keys off this:
 *   - URL has no `country` param → BE 303 redirects to
 *     `?country=France` (when brand has French candidates)
 *   - URL has `country=` (empty) → BE preserves the cleared state and
 *     echoes `filters.country: null` (filter not applied)
 *   - URL has `country=Germany` → BE filters by Germany
 *
 * Because BE echoes `filters.country: null` for BOTH "user cleared"
 * AND "no French candidates → no redirect", the prop alone can't
 * discriminate. The URL is the canonical source of truth — we
 * inspect it to preserve the user-cleared signal across subsequent
 * filter mutations (without it, changing any other filter would
 * strip `?country=` and re-trigger the default-France redirect).
 */
export function urlHasExplicitlyClearedCountry(url: string): boolean {
  const queryStart = url.indexOf("?")
  if (queryStart === -1) return false
  const search = url.slice(queryStart + 1)
  // URLSearchParams.has() returns true even for empty values (e.g.
  // `?country=`); .get() returns the empty string in that case.
  // Both checks together guard against `?country` (no `=`) edge case.
  const params = new URLSearchParams(search)
  return params.has("country") && (params.get("country") ?? "").trim() === ""
}

/**
 * OKS-465 — Detect whether the current URL is in the Removed view
 * (`?removed=1`). The `removed` param is OUTSIDE the filter/sort state
 * this hook otherwise manages, so every navigation it emits MUST
 * re-inject it — otherwise a filter/search/sort/page/clear action while
 * in the Removed view would navigate to bare `candidatesPath()` and
 * silently bounce the user back to the active list (the OKS-465 E2E
 * R2/R3 regression: the search input's debounced onChange firing on
 * mount dropped `?removed=1`, so the just-removed candidate's row
 * vanished before it could be restored).
 */
export function urlHasRemovedView(url: string): boolean {
  const queryStart = url.indexOf("?")
  if (queryStart === -1) return false
  const params = new URLSearchParams(url.slice(queryStart + 1))
  return params.has("removed") && (params.get("removed") ?? "") !== ""
}

/**
 * OKS-713 — Detect whether the Add Candidate sheet is open (`?add=1`).
 *
 * Same class of bug as `urlHasRemovedView` above, and the same fix: `add`
 * lives OUTSIDE the filter/sort state this hook manages, so every
 * navigation it emits MUST re-inject it. Without this, ANY push while the
 * sheet is open (filter, search, sort, page, clear) rewrites the URL to a
 * bare `candidatesPath()` and drops `?add=1` — the next `redirect_back`
 * then lands on a sheet-less page and the user's half-filled draft +
 * inline validation errors are destroyed.
 *
 * This is the real defect behind the OKS-711 "flake": the recruitment-form
 * C3 case was correctly detecting it, not racing a re-render. `openSheet()`
 * (Index.tsx) already gets this right via `{...currentListParams(), add: "1"}`;
 * only this hook's emitters were lossy.
 *
 * Note the asymmetry with `show_add`: `RELOAD_KEYS` deliberately omits it,
 * so a partial reload leaves the prop stale-true and the sheet *looks* fine
 * — the damage only surfaces on the next full visit (e.g. a `redirect_back`
 * off the now-`add`-less Referer). That delay is exactly why this read as a
 * flake for three hardening passes.
 *
 * Accepts a full URL (`/candidates?add=1`) or a bare search string
 * (`?add=1`), so it can be fed `window.location.search` directly.
 */
export function urlHasAddSheet(url: string): boolean {
  const queryStart = url.indexOf("?")
  if (queryStart === -1) return false
  const params = new URLSearchParams(url.slice(queryStart + 1))
  return params.has("add") && (params.get("add") ?? "") !== ""
}

/**
 * OKS-713 — LIVE read of the Add-sheet param, evaluated at call time.
 *
 * MUST be called INSIDE the emitter — never hoisted into the hook body,
 * never a `useCallback` dep.
 *
 * ## What this DOES fix
 *
 * `usePage().url` is a render-scoped snapshot: it only refreshes once
 * Inertia has applied a response AND React has re-rendered. `window.location`
 * is updated by Inertia the moment that response lands — i.e. strictly
 * earlier. So a `push()` firing between "close response landed" and "React
 * re-rendered" reads a STALE `?add=1` from the snapshot and re-injects the
 * param after it was cleared, leaving `show_add=false` against an `add=1`
 * URL (the `RELOAD_KEYS`-omits-`show_add` asymmetry, mirrored) — the sheet
 * springs back open on refresh. Reading live removes THAT window.
 *
 * ## What this does NOT obviously fix — STILL OPEN (read the scope carefully)
 *
 * `closeSheet()` (`pages/Brand/Candidates/Index.tsx:688-700` — NOT this
 * file; the OKS-746 ticket's Refs said otherwise and were wrong) is an
 * **async `router.get`**: it does NOT clear `add` immediately, only when
 * its response lands. So there is an in-flight window where a `push()`
 * live-reads a genuinely-present `?add=1` and re-injects it.
 *
 * An earlier revision of this comment argued that window was probably
 * unreachable, because `@inertiajs/core@2.3.14`'s sync request stream is
 * `{maxConcurrent: 1, interruptible: true}` (:2380), every non-`async`
 * visit calls `interruptInFlight()` before sending (:2497), and
 * `shouldCancel()` is `interruptible && requests.length >= maxConcurrent`
 * (:2372) — so two sync visits "cannot both land".
 *
 * **That argument's unstated premise was that EVERY visit rides that
 * stream. It doesn't** — OKS-746 captured a stranded close with
 * `aborted_requests: 0`, i.e. two visits that both landed. So the premise
 * is dead.
 *
 * **But note precisely what that does and does not tell you.** The visit
 * that collided was Inertia's **deferred-prop batch** — *not* a `push()`,
 * and no re-injection. **So the `push()` window above is NOT resolved by
 * OKS-746; it is exactly as unverified as it was before, minus one bad
 * argument for dismissing it.** (An earlier draft of this very section
 * announced it "RESOLVED by OKS-746 (measured)". That was wrong, and it is
 * this file's signature error: an impressive answer to a *different*
 * question, filed against the original one.)
 *
 * ## LANDED (OKS-849) — the deferred-batch strand is now reconciled
 *
 * The deferred-batch collider described below is closed by a page-scoped
 * guard in `pages/Brand/Candidates/Index.tsx` (the OKS-849 reconcile
 * effect; donor pattern OKS-780 `20db45ce`). On every Inertia `finish` /
 * `navigate` event it reads `window.location.search` LIVE and, when the
 * Sheet is closed (`show_add === false`) but the URL still carries a
 * non-blank `add` marker (`urlHasAddMarker`, mirroring `params[:add]
 * .present?`), strips `add` via `router.replace` — PRESERVING the list
 * params, so filters/sort/`removed`/`country`/page survive. It fires AFTER
 * the deferred's own history write commits, so there is no later queued
 * write to lose to. Deterministic coverage now lives in the suite: the
 * held-request forcing spec (§13.3 harness) is re-added to
 * `candidate-add-flows.spec.ts` — it holds the deferred-props batch open,
 * dismisses, releases, and asserts the settled URL has no `add`. That
 * guard is NOT in this file; this hook's live-read (below) is orthogonal
 * and unchanged. The `push()` in-flight window discussed above is a
 * SEPARATE, in-this-file concern and is NOT what OKS-849 addressed.
 *
 * The collider OKS-746 *did* capture (now guarded by the above):
 *
 *   - `recent_notifications` is an `InertiaRails.defer` shared prop
 *     (`application_controller.rb:184`, gated `if Current.user &&
 *     Current.brand` at `:182`); `funnel` is another
 *     (`candidates_controller.rb:213`).
 *   - Inertia auto-fetches them on mount as ONE batch, born carrying
 *     `window.location.href` at call time (`core:2434-2441`) — so a page
 *     mounted at `?add=1` births a batch stamped `?add=1`.
 *   - It is issued with no `preserveUrl` (`core:2785-2790`; default false
 *     at `:1180`), so it **writes history and re-asserts that dead URL**
 *     if it applies after the close. Its partial omits `show_add`, so the
 *     Sheet stays closed while the URL says open.
 *
 * **Nothing re-injects `add` in that path** — the deferred batch merely
 * carries a URL that stopped being true. So this hook's live-read is not
 * implicated, and this file is not the fix site.
 *
 * Forced deterministically 8/8 red at BOTH `workers=1` and `workers=2` —
 * **no concurrency required**.
 *
 * ⚠️ **Historical note (pre-OKS-849):** that forcing spec used to live only
 * at `Resources/Docs/OKS-746/EVIDENCE-OKS-746.md` **§13.3** — dropped
 * deliberately, not quarantined (`dcf33eed`), because it is red against
 * unfixed code by construction and `test.fixme` is forbidden here
 * (quarantining is what let OKS-711 hide a real bug for three cycles). As
 * of OKS-849 it IS back in the suite (`candidate-add-flows.spec.ts`, the
 * §13.3 held-request harness) and goes green on the landed guard — the
 * "goes green on the fix ticket" this note anticipated.
 *
 * Exposure is a fresh mount already carrying
 * `?add=1` (deep link / shared link / bookmark / refresh); the ordinary
 * click-to-open path has no collider at all (measured: deferred props are
 * fetched only when *missing*, `core:1466-1471`, and `openSheet()`'s
 * `preserveState` partial leaves them defined).
 *
 * ## What the historical green runs of `:395` meant — read this precisely
 *
 * **The URL check's BEHAVIOUR is measured.** `not.toHaveURL` **samples** at
 * ~`0/100/350/850ms` — receipt-verified (`pushstates_fired: 2`), identical
 * on two independently-built bundles (one of which was later found
 * poisoned; re-run clean, same schedule). Event-driven-on-`pushState` is
 * **dead**: had `waitForNavigation` fired on the `pushState` opening the
 * window, the 500ms case would resolve at ~400ms — it resolves at **862ms**,
 * a sample boundary.
 *
 * **The MECHANISM behind that behaviour is consistent with the data but
 * unproven** (an outer retry layer re-running the one-shot check).
 * **Build on the behaviour, not the reconciliation.**
 *
 * **The natural window was observed ONCE (n=1): ~7ms wide, at offset
 * ~110ms** from assertion start (`closeSheet` issued t≈294; applied t=404;
 * deferred applied t=411). At that offset, samples miss.
 *
 * **What the historical greens meant is OPEN — and `n=1` twice over.** The
 * natural distribution of window **offset** AND **width** is unmeasured. An
 * offset sweep shows the outcome inverts across the schedule — offsets
 * ≤100ms **are** caught, offsets ≥110ms are not — so the result turns on
 * *position* at least as much as width. **`P(hollow pass)` across real runs
 * is NOT established.** The reading is **supported at the observed
 * position; not settled.** Do not write "vindicated" in either direction.
 *
 * **The trap that caught the last two attempts — both mine, in opposite
 * directions.** First I wrote *"do not cite past greens as evidence of
 * anything in either direction."* That felt like the maximally cautious
 * line. It wasn't caution — **it was a wrong claim wearing humility**, and
 * refusing to conclude reads as epistemic virtue, so nobody audits it.
 * That made it MORE durable than the confident claims it replaced. Then,
 * on seeing one measurement, I swapped it for *"a sampler essentially never
 * lands in it — past greens are weak-but-real evidence of absence."*
 *
 * **The cure for a hedge that outran its evidence is a MEASUREMENT, not the
 * opposite assertion.** Both were unaudited; I changed the sign, not the
 * epistemics. The honest line was never either one — it is the conditional:
 * greens are weak evidence of absence **IF** the natural offset
 * distribution matches the single observation, **and that distribution is
 * `n=1`.**
 *
 * **Every previous attempt to close this question was the confident one.**
 * It has been argued for three rounds by three people across two reversals;
 * this comment has tried to close it four times. Both a "the greens were
 * hollow" reading and a "the greens were real" reading have been asserted,
 * retracted, and re-asserted here. A synthetic harness — **no app
 * involved** — produced the first hard fact in one run, and even that
 * needed a second sweep to expose that its windows all sat in the
 * schedule's sparsest gap. If you are about to settle it: **measure the
 * natural (offset, width) distribution.** Do not reason about it.
 *
 * ## Why live is still the right shape
 *
 * `window.location` is strictly fresher than `usePage().url`, and it is the
 * SAME source `currentListParams()` feeds `openSheet()`/`closeSheet()`. One
 * source of truth for `add` across every reader and writer means the
 * emitters cannot disagree with their neighbours. That consistency — not
 * any race guarantee — is the reason for this shape.
 *
 * HONESTY NOTE (do not delete — this file's history is why OKS-711 cost
 * three cycles): every mechanism claim here is derived by READING THE
 * SOURCE; none has been observed. This comment's own track record, in one
 * ticket:
 *   1. A reported ~50% outside-click regression against the snapshot
 *      variant — retracted; a port collision was serving another
 *      worktree's app entirely.
 *   2. "`closeSheet()` clears the URL immediately" and "live reading closes
 *      the window by construction" — both false, caught in UX review.
 *   3. "The in-flight window is still reachable" — challenged on a source
 *      trace of Inertia's sync-stream interrupt; recorded as unresolved.
 *      **Still unresolved.** OKS-746 killed the *argument* against it (the
 *      every-visit-rides-the-sync-stream premise) without settling the
 *      question either way.
 *   4. The note about wrong confident claims contained a wrong confident
 *      claim ("three different authors").
 *   5. A draft of the section above announced that window "RESOLVED by
 *      OKS-746 (measured)" — wrong. OKS-746 measured a *different*
 *      collider. An impressive answer to a different question, filed
 *      against the original one.
 *
 * **OKS-746 added roughly a dozen more, across four authors** — including
 * the sync-stream argument above, a forced 8/8 offered as if it falsified
 * a natural rate, and several retractions of retractions (at least one
 * retraction was itself retracted). Every single one was caught by
 * **checking an artifact** — a cited line, a bundle grep, a settled read,
 * a `git status`. **None was caught by argument.** The most persuasive
 * claim on this surface has, repeatedly, been the wrong one — including
 * when the persuasive claim was a *correction*.
 *
 * So: if you are about to add a confident sentence here, the base rate for
 * confident sentences in this comment is poor. Cite a line number, or label
 * it unverified. **Transcribing someone else's unverified claim counts as
 * authoring it.** And note what is NOT claimed above: what the historical
 * green runs meant. That is open. Do not close it from the armchair — this
 * comment has tried four times.
 *
 * A SEPARATE, pre-existing stranded-`?add=1` bug does exist. OKS-746
 * captured **a** mechanism (the deferred batch above). Read the scope of
 * that carefully — the temptation is to file it against every open
 * question at once, which is how this comment got into trouble before:
 *
 *   - **Falsified:** "likely `closeSheet()`'s visit being cancelled by a
 *     concurrent one". `closeSheet()` **lands and APPLIES** — its
 *     `pushState` correctly clears the URL — and is then **overwritten,
 *     not cancelled**. `aborted_requests: 0`. This one is clean.
 *   - **Established, with its instrument named:** the mechanism needs no
 *     concurrency. The **forced** spec (`page.route` holds the deferred
 *     open *by construction*) is 8/8 red at `workers=1` and `workers=2`.
 *     Read "forced" as load-bearing: **determinism is what forcing buys**,
 *     so this says nothing whatever about natural rates.
 *   - **OPEN — do not close this from the armchair:** whether this
 *     mechanism **is** the filed `:395` flake. A **forced** 8/8 does not
 *     falsify a **natural** "~10-20% at `--workers=4`, absent at CI's 2" —
 *     they do not measure the same thing. Reconciling them needs the
 *     natural `(offset, width)` distribution, which is `n=1` (see the
 *     sampler section above — the *schedule* is measured; the *natural
 *     distribution* is not). So that original rate claim is **UNRESOLVED,
 *     not wrong**, and "it's the same bug" is an assumption, not a finding.
 *
 * It does reproduce with ZERO `add` writes in this hook — that part held —
 * so it is NOT caused by this preservation. **If you are here because you
 * saw a stranded `?add=1`, the deferred batch above is the first mechanism
 * to check — and it is not in this file; it is now guarded in
 * `pages/Brand/Candidates/Index.tsx` (OKS-849, see the LANDED note above).**
 * See `Resources/Docs/OKS-746/EVIDENCE-OKS-746.md` (§3 mechanism, §9 the
 * costed entrypoint recommendation, §11 the two falsified fix attempts).
 */
function addSheetIsOpen(): boolean {
  if (typeof window === "undefined") return false
  return urlHasAddSheet(window.location.search)
}

/**
 * URL state mutator for the Candidate List page (OKS-17).
 *
 * The URL is the **single source of truth** for filters/sort/page —
 * no Redux, no client cache, no `useState` mirror. Every mutation
 * rewrites the URL via `router.get` with `preserveState: true` and
 * `preserveScroll: true`, scoped to a `partial reload` of just the
 * data props (keeps brand chip + sidebar mounted).
 *
 * Each setter normalizes its input (drops empties, swaps min>max,
 * resets `page` to 1 on filter mutations so the user doesn't land on
 * page 7 of a freshly-narrowed result). The Backend re-normalizes
 * authoritatively, but the FE's pre-normalize keeps URLs pretty.
 *
 * `partial reload only:` keys are intentionally enumerated — drift in
 * either direction would be caught by Vitest page tests.
 */
const RELOAD_KEYS = [
  "candidates",
  "pagination",
  "filter_options",
  "filters",
  "sort",
  "is_empty",
  // OKS-465 — the Removed-view discriminators must refresh on every
  // partial reload too, otherwise toggling filters in the Removed view
  // would leave `removed_view` / `removed_count` / `view_is_empty` stale.
  "removed_view",
  "removed_count",
  "view_is_empty",
] as const

/**
 * Build the URL params object from the next filter/sort/page state.
 * Drops nullish + empty-string + empty-array values so the URL stays
 * minimal. Arrays are emitted as `key[]` per Rails / Rack conventions.
 */
export function buildCandidateListParams(
  filters: Partial<CandidateFilters>,
  sort: Partial<CandidateSort>,
  page?: number,
): Record<string, string | string[] | number> {
  const params: Record<string, string | string[] | number> = {}

  if (filters.q && filters.q.trim() !== "") params.q = filters.q.trim()
  if (filters.status && filters.status.length > 0)
    params.status = filters.status
  // Country: explicit empty-string is the "user cleared" signal that
  // OPTS OUT of BE's default-France behavior (CTPO V1 lock). Distinct
  // from `country: undefined` (param omitted → BE applies default).
  // `null` is treated as "no preference" → omit param → BE default
  // applies. Empty string `""` is the explicit cleared sentinel and
  // emits `country=` so BE's `params.key?(:country)` check returns
  // true and the default-France block is bypassed.
  if (filters.country !== null && filters.country !== undefined) {
    params.country = filters.country
  }
  if (filters.city && filters.city.trim() !== "")
    params.city = filters.city.trim()
  if (filters.tags && filters.tags.length > 0) params.tags = filters.tags
  // OKS-953 — repeated array params, same wire shape as `status`/`tags`
  // (`?reason_code[]=…`). Absent when empty so a cleared filter leaves no
  // trace in the URL.
  if (filters.reason_code && filters.reason_code.length > 0)
    params.reason_code = filters.reason_code
  if (filters.contact_channel && filters.contact_channel.length > 0)
    params.contact_channel = filters.contact_channel
  if (typeof filters.followers_min === "number")
    params.followers_min = filters.followers_min
  if (typeof filters.followers_max === "number")
    params.followers_max = filters.followers_max
  if (sort.field && sort.field !== "created_at") params.sort = sort.field
  if (sort.dir && sort.dir !== "desc") params.dir = sort.dir
  if (typeof page === "number" && page > 1) params.page = page

  return params
}

/**
 * OKS-743 — canonical, key-order-agnostic form of a URL query string, used to
 * detect a no-op navigation in `push()`. Parses then sorts, so `?a=1&b=2` and
 * `?b=2&a=1` compare equal, and percent-encoding is normalized. A leading `?`
 * is tolerated (`URLSearchParams` handles it).
 */
export function canonicalSearch(search: string): string {
  const sp = new URLSearchParams(search)
  sp.sort()
  return sp.toString()
}

interface UseCandidateListUrlReturn {
  /** Push a new filter/sort/page state to the URL via Inertia partial reload. */
  push: (
    nextFilters: Partial<CandidateFilters>,
    nextSort?: Partial<CandidateSort>,
    nextPage?: number,
  ) => void
  /** Set a single filter (keeps other filters + sort, resets page to 1). */
  setFilter: <K extends keyof CandidateFilters>(
    key: K,
    value: CandidateFilters[K],
  ) => void
  /** Toggle a status filter value (resets page to 1). */
  toggleStatus: (status: CandidateStatus) => void
  /** Set a single status (or clear when null), e.g. for the tabs UI (resets page to 1). */
  setStatusOnly: (status: CandidateStatus | null) => void
  /** Set sort (resets page to 1). */
  setSort: (sort: CandidateSort) => void
  /** Navigate to a different page, preserving filters + sort. */
  setPage: (page: number) => void
  /** Reset everything to defaults (clears all filters, default sort, page 1). */
  clearAll: () => void
}

type StickyReadSource = "snapshot" | "live"

interface StickyParamRule {
  /** URL param this rule re-injects when active. */
  readonly param: "removed" | "add"
  /** Where the "is it active?" read comes from — see the asymmetry note below. */
  readonly read: StickyReadSource
  /**
   * True when the marker is active. Snapshot rules receive the hook-body `url`
   * snapshot; live rules IGNORE the argument and read `window.location`.
   */
  readonly isActive: (urlSnapshot: string) => boolean
}

/**
 * OKS-921 — the single, explicit allow-list of URL params a navigation emitted
 * by this hook PRESERVES (re-injects on every `push`). This is the stated
 * membership rule the hook previously lacked: sticky params were re-injected
 * one accreted `if` at a time (`removed` OKS-465, `add` OKS-713), each landed
 * AFTER a production breakage, with no rule for classifying the next one.
 *
 * MEMBERSHIP RULE: a param survives a `push` IFF it is enumerated here.
 * Everything NOT listed — one-shot / transient markers (`just_added` OKS-127,
 * `bulk_skipped`) and any future unmanaged param — is dropped BY OMISSION: the
 * hook rebuilds the query from its own filter/sort/page state
 * (`buildCandidateListParams`) and re-injects only these entries, so anything
 * else is simply never written back. Adding a future param is therefore a
 * DELIBERATE act — list it (sticky) or leave it off (one-shot).
 *
 * ⚠️ NEVER replace this with a blanket spread of the current URL's params
 * (`{ ...Object.fromEntries(new URLSearchParams(url)) }`). A blanket spread
 * RESURRECTS one-shot markers after they were scrubbed and re-fires their side
 * effects (the `just_added` row highlight, OKS-127) — that resurrection is the
 * exact regression OKS-921 exists to prevent.
 *
 * READ SOURCE — a deliberate, documented asymmetry (OKS-921 KEPT it; do NOT
 * align without a stated concrete gain — aligning re-opens OKS-465's surface):
 *   - `snapshot` — read from the hook-body `usePage().url` snapshot. Safe for
 *     view modes only ever changed by a full navigation this hook itself emits
 *     (`removed`): no out-of-band writer can race them, so the snapshot cannot
 *     be stale in a reachable way, and keeping it out of `push`'s deps
 *     preserves the memoization contract (see the `url` note in the hook body).
 *   - `live` — read from `window.location.search` at call time. Required for
 *     `add`, a transient modal toggle mutated OUT-OF-BAND by `closeSheet()`
 *     (Index.tsx): a snapshot could be stale by the time an emitter fires and
 *     would resurrect `?add=1` after a close. See the long `addSheetIsOpen`
 *     note for the full history behind this read.
 */
export const STICKY_PARAMS: readonly StickyParamRule[] = [
  {
    param: "removed",
    read: "snapshot",
    isActive: (url) => urlHasRemovedView(url),
  },
  { param: "add", read: "live", isActive: () => addSheetIsOpen() },
]

/**
 * Hook that exposes typed setters that emit normalized URL params via
 * Inertia partial reload. Caller passes current props snapshot
 * (filters/sort/pagination) so the hook can compute deltas without
 * also re-reading `usePage()`.
 *
 * Reads `usePage().url` to preserve the explicit "user cleared
 * country" signal across subsequent filter mutations. Without this,
 * the BE-default-France redirect would re-trigger every time the
 * user changed any other filter (because BE echoes
 * `filters.country: null` for both "cleared" and "no French
 * candidates", and the prop alone can't distinguish the two).
 */
export function useCandidateListUrl(
  filters: CandidateFilters,
  sort: CandidateSort,
): UseCandidateListUrlReturn {
  const { url } = usePage()
  // Snapshot at hook call time. Re-reading `url` inside the
  // useCallback would invalidate the memoization on every render;
  // using a closed-over snapshot is fine because Inertia replaces
  // the entire URL on every visit so a stale snapshot is impossible
  // (the hook re-runs with the new URL on each prop update).
  const countryExplicitlyCleared = urlHasExplicitlyClearedCountry(url)
  // Re-inject sticky UI-mode markers per the STICKY_PARAMS allow-list (the
  // membership rule + snapshot/live asymmetry are documented on that const).
  // Snapshot rules (`removed`) are resolved HERE, at hook-call time, so `push`
  // can consult them WITHOUT re-reading `url` inside the callback — which would
  // churn the memo (see the `url` snapshot note above). `snapshotStickyKey` is
  // a stable content string, so `snapshotStickies` keeps a stable reference
  // until the active snapshot set actually flips — exactly the old `removedView`
  // dependency's behavior. Live rules (`add`) are resolved inside `push`.
  const snapshotStickyKey = STICKY_PARAMS.filter(
    (rule) => rule.read === "snapshot" && rule.isActive(url),
  )
    .map((rule) => rule.param)
    .join(",")
  const snapshotStickies = useMemo(
    () => new Set(snapshotStickyKey ? snapshotStickyKey.split(",") : []),
    [snapshotStickyKey],
  )

  // OKS-743 — the reported "spurious RELOAD_KEYS mount-push" is real, but
  // CI-Linux-only. Local macOS Playwright (LSE + LQE, server-restart-verified
  // instrumentation) showed 0 mount-pushes; the `candidate-list-mount.spec.ts`
  // guard nonetheless RED on CI-Linux (`playwright_e2e (4)`) with its positive
  // control GREEN — a real fire, not a broken pin. Mechanism: a Base UI
  // `<Select>` (Country / Sort, both controlled `value` + `onValueChange`)
  // re-emits its CURRENT value as `onValueChange` on mount/hydration under
  // CI-Linux, which routes `setFilter`/`setSort` → `push` with UNCHANGED
  // params. `push()` issued `router.get` unconditionally, so that fired a
  // RELOAD_KEYS partial on every candidate-list mount. (Separately, OKS-849
  // (#628) reconciles the mount-born DEFERRED-PROPS batch —
  // `recent_notifications`/`funnel` — which is a different partial that never
  // carries `filter_options`; that is not this fire.) Fix: the no-op-navigation
  // bail below.
  const push = useCallback(
    (
      nextFilters: Partial<CandidateFilters>,
      nextSort?: Partial<CandidateSort>,
      nextPage?: number,
    ) => {
      // Apply the cleared-country preservation BEFORE merging with the
      // caller's `nextFilters` overrides — explicit caller intent
      // (e.g. setFilter("country", "Germany")) trumps the URL signal.
      const inheritedCountry: Partial<CandidateFilters> =
        countryExplicitlyCleared && filters.country === null
          ? { country: "" }
          : {}
      const merged: CandidateFilters = {
        ...filters,
        ...inheritedCountry,
        ...nextFilters,
      }
      const mergedSort = { ...sort, ...(nextSort ?? {}) }
      const params = buildCandidateListParams(merged, mergedSort, nextPage)
      // Re-inject the sticky allow-list (STICKY_PARAMS). Snapshot rules were
      // resolved at hook-call time (`snapshotStickies`); live rules read
      // window.location NOW — e.g. if `closeSheet()` already cleared `add`, the
      // live read correctly does NOT resurrect it. Params OFF the list (one-shot
      // `just_added` / `bulk_skipped`) are dropped by omission — see the
      // STICKY_PARAMS membership rule.
      for (const rule of STICKY_PARAMS) {
        const active =
          rule.read === "live"
            ? rule.isActive("")
            : snapshotStickies.has(rule.param)
        if (active) params[rule.param] = 1
      }
      // OKS-743 — no-op-navigation bail. Compare the URL this navigation WOULD
      // produce (FINAL params, i.e. AFTER the STICKY_PARAMS `removed` + `add`
      // injections above) against the LIVE address bar. When identical, skip
      // `router.get` — a Base UI `<Select>` re-emitting its current value on
      // mount (CI-Linux) yields exactly this identical-URL case, which must not
      // fire a RELOAD_KEYS partial. Conservative by construction: only a
      // canonically byte-for-byte-identical target is skipped, so a real
      // filter/sort/page change (always a different query) still navigates, and
      // unmanaged params (`bulk_skipped`, etc.) — which a real push already
      // drops — keep their existing behavior (their presence makes the URLs
      // differ, so the bail simply does not apply). `window.location` is the
      // freshest truth and matches the LIVE read `addSheetIsOpen()` uses.
      const nextUrl = candidatesPath(params)
      const nextSearch = nextUrl.includes("?")
        ? nextUrl.slice(nextUrl.indexOf("?"))
        : ""
      if (
        typeof window !== "undefined" &&
        canonicalSearch(nextSearch) === canonicalSearch(window.location.search)
      ) {
        return
      }
      router.get(candidatesPath(), params, {
        preserveState: true,
        preserveScroll: true,
        only: [...RELOAD_KEYS],
      })
    },
    [filters, sort, countryExplicitlyCleared, snapshotStickies],
  )

  const setFilter = useCallback(
    <K extends keyof CandidateFilters>(key: K, value: CandidateFilters[K]) => {
      push({ [key]: value } as Partial<CandidateFilters>, undefined, 1)
    },
    [push],
  )

  const toggleStatus = useCallback(
    (status: CandidateStatus) => {
      const has = filters.status.includes(status)
      const next = has
        ? filters.status.filter((s) => s !== status)
        : [...filters.status, status]
      push({ status: next }, undefined, 1)
    },
    [filters.status, push],
  )

  const setStatusOnly = useCallback(
    (status: CandidateStatus | null) => {
      push({ status: status ? [status] : [] }, undefined, 1)
    },
    [push],
  )

  const setSort = useCallback(
    (nextSort: CandidateSort) => {
      push({}, nextSort, 1)
    },
    [push],
  )

  const setPage = useCallback(
    (page: number) => {
      push({}, undefined, page)
    },
    [push],
  )

  const clearAll = useCallback(() => {
    // `clearAll` bypasses `buildCandidateListParams`, so it re-injects the
    // sticky allow-list itself (same STICKY_PARAMS rule as `push` above):
    // clearing filters in the Removed view stays in the Removed view, and
    // keeps an open Add sheet open. Snapshot rules from `snapshotStickies`,
    // live rules read window.location now; one-shot params drop by omission.
    const params: Record<string, number> = {}
    for (const rule of STICKY_PARAMS) {
      const active =
        rule.read === "live"
          ? rule.isActive("")
          : snapshotStickies.has(rule.param)
      if (active) params[rule.param] = 1
    }
    router.get(candidatesPath(), params, {
      preserveState: true,
      preserveScroll: true,
      only: [...RELOAD_KEYS],
    })
  }, [snapshotStickies])

  return {
    push,
    setFilter,
    toggleStatus,
    setStatusOnly,
    setSort,
    setPage,
    clearAll,
  }
}
